/**
 * RecurPay Integration for Shopify
 * This script handles the integration between flavor selections and the RecurPay subscription widget.
 */

class RecurPayIntegration {
  constructor() {
    this.initialized = false;
    this.selectedItems = [];
    this.currentSellingPlan = '';
    this.defaultSellingPlan = '690910232919'; // Default selling plan ID
    this.debugMode = true; // Enable debug for troubleshooting
    
    // Initialize after DOM is loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }
  
  /**
   * Initialize the integration
   */
  init() {
    if (this.initialized) return;
    
    this.log('Initializing RecurPay integration');
    
    // Set up purchase option radio buttons
    this.setupPurchaseOptions();
    
    // Set up flavor quantity selectors
    this.setupFlavorSelectors();
    
    // Set up RecurPay widget
    this.setupRecurPayWidget();
    
    // Intialize move attempts for RecurPay widget
    this.moveRecurPayWidget();
    
    // Watch for form submissions
    this.setupFormSubmitHandlers();
    
    // Add a MutationObserver to watch for DOM changes
    this.setupMutationObserver();
    
    this.initialized = true;
  }
  
  /**
   * Set up the purchase option radio buttons (subscription vs one-time)
   */
  setupPurchaseOptions() {
    const radioButtons = document.querySelectorAll('.subscription-option__radio');
    if (!radioButtons.length) return;
    
    // Get initial selection
    const initialRadio = document.querySelector('.subscription-option__radio:checked');
    if (initialRadio && initialRadio.value === 'subscription') {
      this.currentSellingPlan = this.defaultSellingPlan;
      // Set initial highlight
      initialRadio.closest('.subscription-option').classList.add('subscription-option__widget-container--selected');
      
      // Show frequency selector for subscription
      const detailsEl = initialRadio.closest('.subscription-option__label').querySelector('.subscription-option__details');
      if (detailsEl) {
        detailsEl.style.display = 'block';
      }
    }
    
    // Add change event listeners to radio buttons
    radioButtons.forEach(radio => {
      // Remove existing listeners for safety
      const newRadio = radio.cloneNode(true);
      radio.parentNode.replaceChild(newRadio, radio);
      
      newRadio.addEventListener('change', () => {
        this.log(`Purchase option changed to: ${newRadio.value}`);
        
        if (newRadio.value === 'subscription') {
          this.currentSellingPlan = this.defaultSellingPlan;
          // Show frequency selector
          const detailsEl = newRadio.closest('.subscription-option__label').querySelector('.subscription-option__details');
          if (detailsEl) {
            detailsEl.style.display = 'block';
          }
        } else {
          this.currentSellingPlan = '';
          // Hide all frequency selectors
          document.querySelectorAll('.subscription-option__details').forEach(el => {
            el.style.display = 'none';
          });
        }
        
        // Update selected items with new selling plan
        this.selectedItems.forEach(item => {
          item.selling_plan = this.currentSellingPlan;
        });
        
        // Highlight selected option
        document.querySelectorAll('.subscription-option').forEach(option => {
          option.classList.remove('subscription-option__widget-container--selected');
        });
        newRadio.closest('.subscription-option').classList.add('subscription-option__widget-container--selected');
        
        // Update RecurPay widget
        this.updateRecurPayWidget();
        
        // Force price update with a small delay and multiple attempts
        setTimeout(() => this.triggerPriceUpdate(), 100);
        setTimeout(() => this.triggerPriceUpdate(), 300);
        setTimeout(() => this.triggerPriceUpdate(), 600);
      });
    });
    
    // Set up frequency selector
    const frequencySelect = document.querySelector('.subscription-option__select');
    const frequencyInput = document.querySelector('.subscription-option__frequency-input');
    if (frequencySelect && frequencyInput) {
      frequencySelect.addEventListener('change', () => {
        frequencyInput.value = frequencySelect.value;
        this.log(`Updated delivery frequency to: ${frequencySelect.value} days`);
        
        // Update RecurPay frequency selector if it exists
        this.updateFrequencySelectors(frequencySelect.value);
        
        // Trigger price update to reflect frequency change
        setTimeout(() => this.triggerPriceUpdate(), 100);
      });
    }
  }
  
  /**
   * Set up flavor quantity selectors
   */
  setupFlavorSelectors() {
    // Set up quantity selectors
    document.querySelectorAll('select.quantity').forEach(select => {
      // Remove existing event listeners if any (prevents multiple handlers)
      const newSelect = select.cloneNode(true);
      select.parentNode.replaceChild(newSelect, select);
      
      newSelect.addEventListener('change', (event) => {
        // Always stop propagation to prevent event bubbling
        event.stopPropagation();
        
        const value = parseInt(newSelect.value, 10);
        
        const flavorEl = newSelect.closest('.products_list');
        if (!flavorEl) return;
        
        const currentId = flavorEl.dataset.id;
        const flavorTitle = flavorEl.querySelector('.products_list_title')?.textContent || 'Product';
        const flavorPrice = parseFloat(flavorEl.dataset.price || '0');
        
        this.log(`Flavor selection changed: ${flavorTitle} (${currentId}) - Quantity: ${value}`);
        
        // Find if this product is already in selectedItems
        const existingIndex = this.selectedItems.findIndex(item => item.id === currentId);
        
        // If quantity is 0 and product exists in array, remove it
        if (value === 0 && existingIndex !== -1) {
          this.selectedItems.splice(existingIndex, 1);
        } 
        // If quantity > 0, update or add
        else if (value > 0) {
          if (existingIndex !== -1) {
            this.selectedItems[existingIndex].quantity = value;
          } else {
            this.selectedItems.push({
              id: currentId,
              title: flavorTitle,
              quantity: value,
              price: flavorPrice,
              selling_plan: this.currentSellingPlan
            });
          }
        }
        
        // Calculate total quantity for shipping message
        const totalQuantity = this.getTotalQuantity();
        
        // Update shipping message
        this.updateShippingMessage(totalQuantity);
        
        // Update RecurPay widget with new selections
        this.updateRecurPayWidget();
        
        // Add a small delay before triggering price update
        setTimeout(() => this.triggerPriceUpdate(), 100);

        
      });
    });
    
    // Set up quantity buttons with improved handling to prevent multiple triggers
    document.querySelectorAll('.quantity-btn').forEach(btn => {
      // Remove existing event listeners if any
      const newBtn = btn.cloneNode(true);
      btn.parentNode.replaceChild(newBtn, btn);
      
      newBtn.addEventListener('click', (event) => {
        // Always prevent default and stop propagation
        event.preventDefault();
        event.stopPropagation();
        const container = newBtn.closest('.quantity-selector');
        const select = container?.querySelector('select.quantity');
        if (!select) return;
        
        let value = parseInt(select.value, 10);
        let newValue = value;
        
        if (newBtn.classList.contains('quantity-decrease')) {
          newValue = Math.max(0, value - 1);
         
        } else if (newBtn.classList.contains('quantity-increase')) {
          newValue = Math.min(5, value + 1);
  
        }
        
        // Only update if value actually changed
        if (newValue !== value) {
          select.value = newValue;
          
          // Create and dispatch a clean change event
          const changeEvent = new CustomEvent('change', { 
            bubbles: false // Prevent bubbling to avoid multiple handlers
          });
          select.dispatchEvent(changeEvent);
        }
      });
    });
    
    // Handle out of stock products
    document.querySelectorAll('.products_list').forEach(flavorEl => {
      const stock = parseInt(flavorEl.dataset.stock || '0', 10);
      const selectEl = flavorEl.querySelector('select.quantity');
      
      if (stock <= 0 && selectEl) {
        selectEl.closest('.quantity-selector').style.display = 'none';
        
        const outOfStockMsg = document.createElement('div');
        outOfStockMsg.textContent = 'Agotado';
        outOfStockMsg.style.color = 'red';
        outOfStockMsg.style.fontWeight = '500';
        outOfStockMsg.style.textAlign = 'center';
        outOfStockMsg.style.margin = '10px 0';
        
        flavorEl.appendChild(outOfStockMsg);
      }
    });
  }
  
  /**
   * Set up RecurPay widget integration
   */
  setupRecurPayWidget() {
    // We'll try to move the RecurPay widget to our container
    setTimeout(() => this.moveRecurPayWidget(), 1000);
    
    // Set up mutation observer to watch for RecurPay widget being added to the DOM
    const observer = new MutationObserver((mutations) => {
      const recurPayAdded = mutations.some(mutation => {
        return Array.from(mutation.addedNodes).some(node => {
          if (node.nodeType === 1) { // Element node
            return node.classList?.contains('recurpay-widget') || node.querySelector?.('.recurpay-widget');
          }
          return false;
        });
      });
      
      if (recurPayAdded) {
        this.log('RecurPay widget detected in DOM, updating settings...');
        // Update widget with a delay to ensure it's fully initialized
        setTimeout(() => {
          this.moveRecurPayWidget();
          if (this.selectedItems.length > 0) {
            this.updateRecurPayWidget();
          } else {
            this.toggleRecurPayButtonVisibility(false);
          }
        }, 300);
      }
    });
    
    observer.observe(document.body, { childList: true, subtree: true });
  }
  
  /**
   * Move RecurPay widget to our custom container
   */
  moveRecurPayWidget() {
    try {
      const recurPayWidget = document.querySelector('.recurpay-widget');
      const recurPayContainer = document.querySelector('.recurpay-container');
      
      if (recurPayWidget && recurPayContainer && !recurPayContainer.contains(recurPayWidget)) {
        this.log('Moving RecurPay widget into our container');
        recurPayContainer.appendChild(recurPayWidget);
        
        // Make sure widget is visible
        recurPayWidget.style.opacity = '1';
        recurPayWidget.style.visibility = 'visible';
        recurPayWidget.style.display = 'block';
        
        // Add attribute to track that we've moved it
        recurPayWidget.setAttribute('data-moved', 'true');
        
        // Add missing form inputs if needed
        this.addMissingFormInputs(recurPayWidget);
        
        // Update widget with current selections
        if (this.selectedItems.length > 0) {
          this.updateRecurPayWidget();
        } else {
          this.toggleRecurPayButtonVisibility(false);
        }
        
        return true;
      } else if (!recurPayWidget) {
        this.log('RecurPay widget not found yet, will try again later');
        setTimeout(() => this.moveRecurPayWidget(), 1000);
        return false;
      } else if (recurPayContainer && recurPayContainer.contains(recurPayWidget)) {
        this.log('RecurPay widget already in container');
        
        // Make sure it's visible
        recurPayWidget.style.opacity = '1';
        recurPayWidget.style.visibility = 'visible';
        recurPayWidget.style.display = 'block';
        
        // Update it if needed
        if (this.selectedItems.length > 0 && !recurPayWidget.getAttribute('data-updated')) {
          this.updateRecurPayWidget();
          recurPayWidget.setAttribute('data-updated', 'true');
        }
        
        return true;
      }
    } catch (e) {
      this.error('Error moving RecurPay widget: ' + e.message);
      return false;
    }
  }
  
  /**
   * Add missing inputs to RecurPay form
   */
  addMissingFormInputs(recurPayWidget) {
    const recurPayForm = recurPayWidget.querySelector('form');
    if (!recurPayForm) return;
    
    // Add quantity input if missing
    let quantityInput = recurPayForm.querySelector('input[name="quantity"]');
    if (!quantityInput) {
      quantityInput = document.createElement('input');
      quantityInput.type = 'hidden';
      quantityInput.name = 'quantity';
      quantityInput.value = '1';
      recurPayForm.appendChild(quantityInput);
    }
    
    // Update quantity with actual total
    const totalQuantity = this.getTotalQuantity();
    if (totalQuantity > 0) {
      quantityInput.value = totalQuantity;
    }
    
    // Add purchase option attribute if missing
    if (!recurPayForm.querySelector('input[name="attributes[purchase_option]"]')) {
      const purchaseOptionInput = document.createElement('input');
      purchaseOptionInput.type = 'hidden';
      purchaseOptionInput.name = 'attributes[purchase_option]';
      purchaseOptionInput.value = document.querySelector('.subscription-option__radio:checked')?.value || 'one-time';
      recurPayForm.appendChild(purchaseOptionInput);
    }
  }
  
  /**
   * Update RecurPay widget with current selections
   */
  updateRecurPayWidget() {
    this.log('Updating RecurPay widget with current selections');
    
    // Get current subscription status
    const subscriptionRadio = document.querySelector('.subscription-option__radio[value="subscription"]');
    const isSubscription = subscriptionRadio ? subscriptionRadio.checked : false;
    const sellingPlan = isSubscription ? this.defaultSellingPlan : '';
    
    this.log(`Subscription status: ${isSubscription ? 'Subscription' : 'One-time purchase'}`);
    
    // Exit if no items selected
    if (this.selectedItems.length === 0) {
      this.log('No flavors selected, disabling RecurPay button');
      this.toggleRecurPayButtonVisibility(false);
      return;
    }
    
    // Enable RecurPay button
    this.toggleRecurPayButtonVisibility(true);
    
    // Get the selected flavor (first in array)
    const selectedFlavor = this.selectedItems[0];
    this.log(`Selected flavor: ${selectedFlavor.title} | ID: ${selectedFlavor.id} | Selling plan: ${sellingPlan}`);
    
    // Calculate total quantity
    const totalQuantity = this.getTotalQuantity();
    
    // Find RecurPay container
    const recurPayContainer = document.querySelector('.recurpay-widget');
    if (!recurPayContainer) {
      this.log('RecurPay widget not found, cannot update');
      return;
    }
    
    // Update variant ID in all forms
    document.querySelectorAll('input[name="id"]').forEach(input => {
      if (input.value !== selectedFlavor.id) {
        input.value = selectedFlavor.id;
        input.disabled = false;
        
        try {
          input.dispatchEvent(new Event('change', { bubbles: true }));
        } catch (e) {
          this.error('Error dispatching change event to variant input: ' + e.message);
        }
      }
    });
    
    // Update quantity in all cart forms
    document.querySelectorAll('input[name="quantity"]').forEach(input => {
      if (!input.closest('.quantity-selector')) { // Skip our flavor quantity selectors
        if (input.value !== totalQuantity.toString()) {
          input.value = totalQuantity;
          try {
            input.dispatchEvent(new Event('change', { bubbles: true }));
            input.dispatchEvent(new Event('input', { bubbles: true }));
          } catch (e) {
            this.error('Error dispatching events to quantity input: ' + e.message);
          }
        }
      }
    });
    
    // Update selling plan in all forms
    document.querySelectorAll('input[name="selling_plan"]').forEach(input => {
      if (input.value !== sellingPlan) {
        input.value = sellingPlan;
        try {
          input.dispatchEvent(new Event('change', { bubbles: true }));
        } catch (e) {
          this.error('Error dispatching change event to selling plan input: ' + e.message);
        }
      }
    });
    
    // Update RecurPay radio buttons for purchase type
    const recurPayRadios = recurPayContainer.querySelectorAll('input[type="radio"], input[name="purchase_type"]');
    if (recurPayRadios.length >= 2) {
      // Select the appropriate radio (first is usually one-time, second is subscription)
      const radioToSelect = isSubscription ? recurPayRadios[1] : recurPayRadios[0];
      
      if (radioToSelect && !radioToSelect.checked) {
        this.log(`Setting RecurPay purchase option to: ${isSubscription ? 'subscription' : 'one-time'}`);
        radioToSelect.checked = true;
        
        try {
          // Trigger all relevant events
          ['change', 'click'].forEach(eventType => {
            const event = new Event(eventType, { bubbles: true });
            radioToSelect.dispatchEvent(event);
          });
        } catch (e) {
          this.error('Error dispatching events to RecurPay radio: ' + e.message);
        }
      }
    }
    
    // Update frequency selectors if needed
    if (isSubscription) {
      const frequencyInput = document.querySelector('.subscription-option__frequency-input');
      if (frequencyInput) {
        this.updateFrequencySelectors(frequencyInput.value);
      }
    }
    
    // Trigger variant change events for theme compatibility
    try {
      const variantChangedEvent = new CustomEvent('variant:changed', {
        detail: { 
          variant: { 
            id: selectedFlavor.id,
            selling_plan_id: sellingPlan || null
          } 
        },
        bubbles: true
      });
      
      document.dispatchEvent(variantChangedEvent);
      
      // Trigger price update immediately and then again after a delay
      this.triggerPriceUpdate();
      setTimeout(() => this.triggerPriceUpdate(), 300);
    } catch (e) {
      this.error('Error dispatching variant change event: ' + e.message);
    }
  }
  
  /**
   * Update all frequency selectors to match the given value
   */
  updateFrequencySelectors(frequency) {
    const recurPayFrequencySelects = document.querySelectorAll('.recurpay-widget select[name*="frequency"]');
    if (recurPayFrequencySelects.length > 0) {
      recurPayFrequencySelects.forEach(select => {
        const matchingOption = Array.from(select.options).find(option => 
          option.value === frequency || option.text.includes(frequency)
        );
        
        if (matchingOption) {
          select.value = matchingOption.value;
          select.dispatchEvent(new Event('change'));
        }
      });
    }
  }
  
  /**
   * Show/hide RecurPay button based on whether flavors are selected
   */
  toggleRecurPayButtonVisibility(show) {
    const recurPayContainer = document.querySelector('.recurpay-widget');
    if (!recurPayContainer) return;
    
    const recurPayForm = recurPayContainer.querySelector('form');
    if (!recurPayForm) return;
    
    const button = recurPayForm.querySelector('button[type="submit"]');
    if (button) {
      if (show) {
        button.disabled = false;
        button.style.opacity = '1';
        button.style.cursor = 'pointer';
      } else {
        button.disabled = true;
        button.style.opacity = '0.5';
        button.style.cursor = 'not-allowed';
      }
    }
  }
  
  /**
   * Update shipping message based on quantity
   */
  updateShippingMessage(totalQuantity) {
    const msgText = document.querySelector('.shipping_msg');
    if (!msgText) return;
    
    if (totalQuantity === 0) {
      msgText.innerHTML = '&nbsp;';
    } else if (totalQuantity === 1) {
      msgText.textContent = 'Añade otra bolsa para envío gratis!';
      msgText.style.color = '#000';
    } else {
      msgText.textContent = 'Tienes envío gratuito!';
      msgText.style.color = '#000';
    }
  }
  
  /**
   * Get total quantity across all selected items
   */
  getTotalQuantity() {
    return this.selectedItems.reduce((total, item) => 
      total + parseInt(item.quantity || 0, 10), 0
    );
  }
  
  /**
   * Trigger price update in RecurPay widget
   */
  triggerPriceUpdate() {
    this.log('Triggering price update');
    
    // Find RecurPay widget
    const recurPayContainer = document.querySelector('.recurpay-widget');
    if (!recurPayContainer) return;
    
    // Get subscription status to determine which price should be shown
    const subscriptionRadio = document.querySelector('.subscription-option__radio[value="subscription"]');
    const isSubscription = subscriptionRadio ? subscriptionRadio.checked : false;
    
    // Get total quantity to ensure it's correctly reflected
    const totalQuantity = this.getTotalQuantity();
    this.log(`Total quantity for price update: ${totalQuantity}`);
    
    // Update all quantity inputs throughout the page
    document.querySelectorAll('input[name="quantity"]').forEach(input => {
      if (!input.closest('.quantity-selector')) { // Skip our flavor quantity selectors
        if (input.value !== totalQuantity.toString()) {
          this.log(`Updating quantity input from ${input.value} to ${totalQuantity}`);
          input.value = totalQuantity;
          
          // Trigger events to notify any listeners
          ['change', 'input', 'blur'].forEach(eventName => {
            try {
              const event = new Event(eventName, { bubbles: true });
              input.dispatchEvent(event);
            } catch (e) {
              this.error(`Error dispatching ${eventName} event: ${e.message}`);
            }
          });
        }
      }
    });
    
    // Update purchase type radios in RecurPay widget
    const recurPayRadios = recurPayContainer.querySelectorAll('input[type="radio"], input[name="purchase_type"]');
    if (recurPayRadios.length >= 2) {
      // Select the appropriate radio (first is usually one-time, second is subscription)
      const radioToSelect = isSubscription ? recurPayRadios[1] : recurPayRadios[0];
      
      if (radioToSelect) {
        if (!radioToSelect.checked) {
          this.log(`Setting RecurPay purchase option to: ${isSubscription ? 'subscription' : 'one-time'}`);
          radioToSelect.checked = true;
        }
        
        // Force click event first, then change - order is important for some implementations
        try {
          radioToSelect.dispatchEvent(new Event('click', { bubbles: true }));
          radioToSelect.dispatchEvent(new Event('change', { bubbles: true }));
        } catch (e) {
          this.error('Error dispatching events to RecurPay radio: ' + e.message);
        }
      }
    }
    
    // Try to trigger any custom events or methods RecurPay might use
    try {
      // Custom events that might trigger price updates
      document.dispatchEvent(new CustomEvent('variant:change', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('variant:updated', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('product:price:update', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
      
      // Force update of the RecurPay widget if it has a global updatePrice method
      if (typeof window.updateRecurPayPrice === 'function') {
        window.updateRecurPayPrice();
      }
      
      // Try to directly update RecurPay price elements
      const priceElements = recurPayContainer.querySelectorAll('.price, [data-price], .product-price');
      if (priceElements.length > 0) {
        // Make price elements visible and properly styled
        priceElements.forEach(el => {
          el.style.visibility = 'visible';
          el.style.display = 'block';
          el.style.fontWeight = 'bold';
          el.style.fontSize = '16px';
        });
      }
    } catch (e) {
      this.error('Error dispatching custom events: ' + e.message);
    }
  }
  
  /**
   * Set up form submit handlers
   */
  setupFormSubmitHandlers() {
    document.addEventListener('submit', (e) => {
      const form = e.target;
      
      // Check if this is a RecurPay form or a cart form
      if (form.closest('.recurpay-widget') || form.action.includes('/cart/add')) {
        e.preventDefault();
        e.stopPropagation();
        
        // Calculate total selected items directly from the DOM for accuracy
        const selectedProducts = [];
        let totalQuantity = 0;
        
        document.querySelectorAll('.products_list').forEach(product => {
          const quantitySelect = product.querySelector('select.quantity');
          const quantity = parseInt(quantitySelect?.value || '0', 10);
          
          if (quantity > 0) {
            selectedProducts.push({
              id: product.dataset.id,
              title: product.querySelector('.products_list_title').textContent,
              quantity: quantity
            });
            
            totalQuantity += quantity;
          }
        });
        
        // If no products selected, prevent submission
        if (selectedProducts.length === 0 || totalQuantity === 0) {
          this.log('Preventing form submission: No flavors selected');
          alert('Por favor, selecciona al menos un sabor antes de añadir al carrito.');
          return false;
        }
        
        // Make sure RecurPay form has the latest variant and selling plan
        this.updateRecurPayWidget();
        
        // Ensure quantity input has the correct value
        let quantityInput = form.querySelector('input[name="quantity"]');
        if (!quantityInput) {
          quantityInput = document.createElement('input');
          quantityInput.type = 'hidden';
          quantityInput.name = 'quantity';
          form.appendChild(quantityInput);
        }
        quantityInput.value = totalQuantity;
        this.log(`Setting final quantity to ${totalQuantity} before submission`);
        
        // Add cart attributes to store the purchase option
        const purchaseOption = document.querySelector('.subscription-option__radio:checked')?.value || 'one-time';
        
        // Set variant ID to first selected product
        const variantInput = form.querySelector('input[name="id"]');
        if (variantInput && selectedProducts.length > 0) {
          variantInput.value = selectedProducts[0].id;
          this.log(`Setting variant ID to ${selectedProducts[0].id}`);
        }
        
        // Try to add the purchase option as a cart attribute
        try {
          // Check if we need to add a hidden input for the purchase option
          let purchaseOptionInput = form.querySelector('input[name="attributes[purchase_option]"]');
          if (!purchaseOptionInput) {
            purchaseOptionInput = document.createElement('input');
            purchaseOptionInput.type = 'hidden';
            purchaseOptionInput.name = 'attributes[purchase_option]';
            form.appendChild(purchaseOptionInput);
          }
          purchaseOptionInput.value = purchaseOption;
          
          // If this is a subscription, ensure the selling plan is set
          if (purchaseOption === 'subscription') {
            const sellingPlanInput = form.querySelector('input[name="selling_plan"]');
            if (sellingPlanInput) {
              sellingPlanInput.value = this.defaultSellingPlan;
            }
            
            // Also add properties for subscription
            let planNameInput = form.querySelector('input[name="properties[Plan]"]');
            if (!planNameInput) {
              planNameInput = document.createElement('input');
              planNameInput.type = 'hidden';
              planNameInput.name = 'properties[Plan]';
              form.appendChild(planNameInput);
            }
            planNameInput.value = 'Subscription';
            
            let planIdInput = form.querySelector('input[name="properties[_PlanId]"]');
            if (!planIdInput) {
              planIdInput = document.createElement('input');
              planIdInput.type = 'hidden';
              planIdInput.name = 'properties[_PlanId]';
              form.appendChild(planIdInput);
            }
            planIdInput.value = this.defaultSellingPlan;
          }
        } catch (e) {
          this.error('Error adding purchase option to form: ' + e.message);
        }
        
        this.log('Form submission allowed - submitting form now');
        setTimeout(() => {
          form.submit();
        }, 100);
      }
    }, true);
  }
  
  /**
   * Set up mutation observer to watch for changes to RecurPay widget
   */
  setupMutationObserver() {
    // Watch for RecurPay widget being hidden or removed
    setInterval(() => {
      const recurPayWidget = document.querySelector('.recurpay-widget');
      if (recurPayWidget && recurPayWidget.getAttribute('data-moved') === 'true') {
        // If the widget is being hidden, force it visible again
        if (recurPayWidget.style.display === 'none' || 
            recurPayWidget.style.visibility === 'hidden' || 
            recurPayWidget.style.opacity === '0') {
          recurPayWidget.style.display = 'block';
          recurPayWidget.style.visibility = 'visible';
          recurPayWidget.style.opacity = '1';
        }
      } else {
        // If widget is not found or has been removed, try to move it again
        this.moveRecurPayWidget();
      }
    }, 1000);
    
    // Watch for price changes in the RecurPay widget
    const priceObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList' || mutation.type === 'characterData') {
          this.log('Price may have changed in RecurPay widget');
          
          // Check if we need to update the price again
          const subscriptionRadio = document.querySelector('.subscription-option__radio[value="subscription"]');
          const isSubscription = subscriptionRadio ? subscriptionRadio.checked : false;
          
          // Make sure correct purchase option is selected in RecurPay
          const recurPayWidget = document.querySelector('.recurpay-widget');
          if (recurPayWidget) {
            const recurPayRadios = recurPayWidget.querySelectorAll('input[type="radio"], input[name="purchase_type"]');
            if (recurPayRadios.length >= 2) {
              const radioToSelect = isSubscription ? recurPayRadios[1] : recurPayRadios[0];
              if (radioToSelect && !radioToSelect.checked) {
                radioToSelect.checked = true;
                radioToSelect.dispatchEvent(new Event('change', { bubbles: true }));
              }
            }
          }
        }
      }
    });
    
    // Start observing price containers for changes
    setTimeout(() => {
      const recurPayWidget = document.querySelector('.recurpay-widget');
      if (recurPayWidget) {
        const priceElements = recurPayWidget.querySelectorAll('.price, [data-price], .product-price');
        priceElements.forEach(el => {
          priceObserver.observe(el, { childList: true, characterData: true, subtree: true });
        });
      }
    }, 2000);
  }
  
  /**
   * Log a message (only in debug mode)
   */
  log(message) {
    if (this.debugMode) {
      // console.log(`[RecurPay Integration] ${message}`);
    }
  }
  
  /**
   * Log an error
   */
  error(message) {
    console.error(`[RecurPay Integration] ERROR: ${message}`);
  }
}

// Initialize the integration
const recurPayIntegration = new RecurPayIntegration(); 